import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import { API_BASE_URL } from "../config/apiConfig";

/**
 * Effective permissions for the signed-in user.
 *
 * The server is authoritative - every guarded endpoint re-checks the matrix via
 * [RequirePermission]. What this context provides is the same answer up front so
 * the UI can avoid rendering things the user cannot use.
 *
 * Replaces the old `user?.role === "Admin"` / `user?.userId === 41` checks that
 * were spread across the routes and the sidebar.
 */

const PermissionContext = createContext(null);

const CACHE_KEY = "erp_permissions_cache";

const readCache = () => {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const PermissionProvider = ({ children }) => {
  const user = useSelector((state) => state.auth.user);
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);

  const cached = readCache();
  const [grants, setGrants] = useState(() => new Set(cached?.grants || []));
  const [menu, setMenu] = useState(cached?.menu || []);
  const [isSuperAdmin, setIsSuperAdmin] = useState(cached?.isSuperAdmin || false);
  const [tenant, setTenant] = useState(cached?.tenant || { id: null, name: null });
  const [loading, setLoading] = useState(true);

  const apply = useCallback((permissions, menuNodes, superFlag, tenantInfo) => {
    const list = Array.isArray(permissions) ? permissions : [];
    const nodes = Array.isArray(menuNodes) ? menuNodes : [];
    const t = tenantInfo || { id: null, name: null };

    setGrants(new Set(list));
    setMenu(nodes);
    setIsSuperAdmin(Boolean(superFlag));
    setTenant(t);

    sessionStorage.setItem(
      CACHE_KEY,
      JSON.stringify({
        grants: list,
        menu: nodes,
        isSuperAdmin: Boolean(superFlag),
        tenant: t,
      })
    );
  }, []);

  const refresh = useCallback(async () => {
    if (!isLoggedIn) {
      setGrants(new Set());
      setMenu([]);
      setIsSuperAdmin(false);
      setTenant({ id: null, name: null });
      sessionStorage.removeItem(CACHE_KEY);
      setLoading(false);
      return;
    }

    try {
      const { data } = await axios.get(
        `${API_BASE_URL}/api/AccessControl/my_permissions`
      );
      apply(
        data?.permissions || data?.Permissions,
        data?.menu || data?.Menu,
        data?.isSuperAdmin ?? data?.IsSuperAdmin,
        { id: data?.tenantId ?? null, name: data?.tenantName ?? null }
      );
    } catch {
      // Keep the cached set; the axios interceptor already handles a hard 401.
    } finally {
      setLoading(false);
    }
  }, [isLoggedIn, apply]);

  // The login response already carries permissions, so seed from it to avoid a
  // visible flash of an empty sidebar, then confirm against the server.
  useEffect(() => {
    if (isLoggedIn && user?.permissions) {
      apply(user.permissions, user.menu, user.isSuperAdmin, {
        id: user.tenantId ?? null,
        name: user.tenantName ?? null,
      });
      setLoading(false);
    }
    refresh();
  }, [isLoggedIn, user?.userId]); // eslint-disable-line react-hooks/exhaustive-deps

  const can = useCallback(
    (moduleCode, permission = "VIEW") => {
      if (isSuperAdmin) return true;
      if (!moduleCode) return false;
      return grants.has(`${moduleCode}:${permission}`);
    },
    [grants, isSuperAdmin]
  );

  const canAny = useCallback(
    (moduleCodes = [], permission = "VIEW") => {
      if (isSuperAdmin) return true;
      return moduleCodes.some((code) => grants.has(`${code}:${permission}`));
    },
    [grants, isSuperAdmin]
  );

  /** Route path -> module code, derived from the menu the server sent. */
  const routeModuleMap = useMemo(() => {
    const map = {};
    menu.forEach((node) => {
      if (node.routePath) map[node.routePath] = node.moduleCode;
    });
    return map;
  }, [menu]);

  /** Menu assembled into parent/child form for the sidebar. */
  const menuTree = useMemo(() => {
    const byId = {};
    const roots = [];

    menu.forEach((node) => {
      byId[node.moduleId] = { ...node, children: [] };
    });

    menu.forEach((node) => {
      const current = byId[node.moduleId];
      if (node.parentModuleId && byId[node.parentModuleId]) {
        byId[node.parentModuleId].children.push(current);
      } else {
        roots.push(current);
      }
    });

    const sortNodes = (nodes) => {
      nodes.sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
      nodes.forEach((n) => sortNodes(n.children));
      return nodes;
    };

    // A parent with a route of its own and no visible children is a leaf; a
    // parent with neither route nor children should not render at all.
    return sortNodes(roots).filter((n) => n.routePath || n.children.length > 0);
  }, [menu]);

  const value = useMemo(
    () => ({
      loading,
      isSuperAdmin,
      /** Null id = the provider, who is not inside any tenant. */
      tenant,
      isProvider: isSuperAdmin,
      grants,
      menu,
      menuTree,
      routeModuleMap,
      can,
      canAny,
      refresh,
    }),
    [loading, isSuperAdmin, tenant, grants, menu, menuTree, routeModuleMap, can, canAny, refresh]
  );

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};

export const usePermissions = () => {
  const ctx = useContext(PermissionContext);
  if (!ctx) {
    throw new Error("usePermissions must be used inside <PermissionProvider>");
  }
  return ctx;
};

/**
 * Conditional renderer.
 *
 *   <Can module="MST_COMPANY" permission="EDIT">
 *     <button>Edit</button>
 *   </Can>
 */
export const Can = ({ module, permission = "VIEW", fallback = null, children }) => {
  const { can } = usePermissions();
  return can(module, permission) ? <>{children}</> : fallback;
};

export default PermissionContext;
